/**
 * Gobodo Approval Process Service
 *
 * CRUD for ApprovalProcess__c via Gobodo API. Stores full process definition
 * in DefinitionJson__c; scalars in Name__c, Description__c, ObjectType__c, IsActive__c, Order__c.
 */

import type { ApprovalProcess, CreateApprovalProcessPayload, UpdateApprovalProcessPayload } from '../../types/approvalProcesses';
import {
  getGobodoRecords,
  getGobodoRecordById,
  createGobodoRecord,
  updateGobodoRecord,
  deleteGobodoRecord
} from './gobodoRecordService';
import { ensureObjectTypeExists } from '../../utils/gobodoObjectTypeVerifier';
import { getCustomSuffix } from '../../config/gobodoConfig';

const getObjectType = () => `ApprovalProcess${getCustomSuffix()}`;

export async function ensureApprovalProcessObjectExists(): Promise<void> {
  await ensureObjectTypeExists(getObjectType(), {
    label: 'Approval Process',
    pluralLabel: 'Approval Processes',
    description: 'Multi-step approval definition, steps, criteria.',
    icon: 'CheckSquare',
    category: 'custom',
    isCustom: true,
    isActive: true
  });
}

const FIELD = {
  NAME: 'Name__c',
  DESCRIPTION: 'Description__c',
  OBJECT_TYPE: 'ObjectType__c',
  IS_ACTIVE: 'IsActive__c',
  ORDER: 'Order__c',
  DEFINITION_JSON: 'DefinitionJson__c',
  CREATED_DATE: 'CreatedDate__c',
  LAST_MODIFIED: 'LastModified__c',
  CREATED_BY: 'CreatedBy__c',
  LAST_MODIFIED_BY: 'LastModifiedBy__c'
} as const;

function approvalProcessToGobodoRecord(process: Partial<ApprovalProcess>): Record<string, unknown> {
  const record: Record<string, unknown> = {
    [FIELD.NAME]: process.name ?? '',
    [FIELD.DESCRIPTION]: process.description ?? '',
    [FIELD.OBJECT_TYPE]: process.objectType ?? '',
    [FIELD.IS_ACTIVE]: process.isActive ?? true,
    [FIELD.ORDER]: process.order ?? 0,
    [FIELD.CREATED_BY]: process.createdBy ?? '',
    [FIELD.LAST_MODIFIED_BY]: process.lastModifiedBy ?? ''
  };
  if (process.createdDate) record[FIELD.CREATED_DATE] = process.createdDate;
  if (process.lastModifiedDate) record[FIELD.LAST_MODIFIED] = process.lastModifiedDate;
  const def = {
    entryCriteria: process.entryCriteria,
    entryCriteriaLogic: process.entryCriteriaLogic,
    steps: process.steps,
    initialSubmissionActions: process.initialSubmissionActions,
    finalApprovalActions: process.finalApprovalActions,
    finalRejectionActions: process.finalRejectionActions,
    recallActions: process.recallActions,
    allowRecall: process.allowRecall,
    recordEditability: process.recordEditability,
    approverCanEdit: process.approverCanEdit,
    approvalEmailTemplateId: process.approvalEmailTemplateId,
    rejectionEmailTemplateId: process.rejectionEmailTemplateId
  };
  record[FIELD.DEFINITION_JSON] = JSON.stringify(def);
  return record;
}

function gobodoRecordToApprovalProcess(record: Record<string, any>): ApprovalProcess {
  const id = record.id != null ? String(record.id) : '';
  let def: {
    entryCriteria?: ApprovalProcess['entryCriteria'];
    entryCriteriaLogic?: ApprovalProcess['entryCriteriaLogic'];
    steps?: ApprovalProcess['steps'];
    initialSubmissionActions?: ApprovalProcess['initialSubmissionActions'];
    finalApprovalActions?: ApprovalProcess['finalApprovalActions'];
    finalRejectionActions?: ApprovalProcess['finalRejectionActions'];
    recallActions?: ApprovalProcess['recallActions'];
    allowRecall?: boolean;
    recordEditability?: ApprovalProcess['recordEditability'];
    approverCanEdit?: boolean;
    approvalEmailTemplateId?: string;
    rejectionEmailTemplateId?: string;
  } = {};
  try {
    const raw = record[FIELD.DEFINITION_JSON];
    if (raw && typeof raw === 'string') def = JSON.parse(raw);
    else if (raw && typeof raw === 'object') def = raw;
  } catch {
    // keep default
  }
  return {
    id,
    name: record[FIELD.NAME] ?? '',
    description: record[FIELD.DESCRIPTION] ?? '',
    objectType: record[FIELD.OBJECT_TYPE] ?? '',
    isActive: Boolean(record[FIELD.IS_ACTIVE]),
    order: Number(record[FIELD.ORDER]) || 0,
    entryCriteria: def.entryCriteria ?? [],
    entryCriteriaLogic: def.entryCriteriaLogic ?? 'AND',
    steps: def.steps ?? [],
    initialSubmissionActions: def.initialSubmissionActions ?? [],
    finalApprovalActions: def.finalApprovalActions ?? [],
    finalRejectionActions: def.finalRejectionActions ?? [],
    recallActions: def.recallActions ?? [],
    allowRecall: def.allowRecall ?? true,
    recordEditability: def.recordEditability ?? 'locked',
    approverCanEdit: def.approverCanEdit ?? false,
    approvalEmailTemplateId: def.approvalEmailTemplateId,
    rejectionEmailTemplateId: def.rejectionEmailTemplateId,
    createdDate: record[FIELD.CREATED_DATE] ?? new Date().toISOString(),
    createdBy: record[FIELD.CREATED_BY] ?? '',
    lastModifiedDate: record[FIELD.LAST_MODIFIED] ?? new Date().toISOString(),
    lastModifiedBy: record[FIELD.LAST_MODIFIED_BY] ?? ''
  };
}

export async function getApprovalProcesses(): Promise<ApprovalProcess[]> {
  await ensureApprovalProcessObjectExists();
  const { records } = await getGobodoRecords(getObjectType());
  return records.map((r: Record<string, any>) => gobodoRecordToApprovalProcess(r));
}

export async function getApprovalProcessById(id: string): Promise<ApprovalProcess | null> {
  const record = await getGobodoRecordById(id);
  if (!record) return null;
  return gobodoRecordToApprovalProcess(record);
}

function generateId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export async function createApprovalProcess(payload: CreateApprovalProcessPayload): Promise<ApprovalProcess> {
  const now = new Date().toISOString();
  const steps: ApprovalProcess['steps'] = payload.steps.map((step, index) => ({
    ...step,
    id: generateId('step'),
    stepNumber: index + 1
  }));
  const process: Partial<ApprovalProcess> = {
    ...payload,
    steps,
    createdDate: now,
    createdBy: 'current_user',
    lastModifiedDate: now,
    lastModifiedBy: 'current_user'
  };
  const gobodoPayload = approvalProcessToGobodoRecord(process);
  const created = await createGobodoRecord(getObjectType(), gobodoPayload);
  return gobodoRecordToApprovalProcess(created);
}

export async function updateApprovalProcess(payload: UpdateApprovalProcessPayload): Promise<ApprovalProcess | null> {
  const existingProcess = await getApprovalProcessById(payload.id);
  if (!existingProcess) return null;
  const merged: ApprovalProcess = { ...existingProcess, ...payload };
  const gobodoPayload = approvalProcessToGobodoRecord(merged);
  const updated = await updateGobodoRecord(payload.id, gobodoPayload);
  return gobodoRecordToApprovalProcess(updated);
}

export async function deleteApprovalProcess(id: string): Promise<boolean> {
  await deleteGobodoRecord(id);
  return true;
}
